/**
 * PeerTube's wire format, measured against the live default instance (2026-08-03, framatube.org and
 * sepiasearch.org, both CORS open — `access-control-allow-origin: *`,
 * `access-control-allow-credentials: true`).
 *
 * - `GET /api/v1/search/videos?search={title}&count={n}&searchTarget={local|search-index}` answers
 *   `{ total, data: [...] }`; a row carries `{ name, duration, uuid, shortUUID, embedPath, account:
 *   { host }, channel: { host }, embedUrl?, … }` — `duration` in whole seconds, `embedPath` the
 *   instance-relative embed path. `account.host` (falling back to `channel.host`) is the row's own
 *   origin — on a `search-index` search this is routinely a *different* server than the one asked to
 *   search (measured: searching sepiasearch.org for "Big Buck Bunny" answered hits on
 *   `video.blender.org`, `videos.domainepublic.net`, `videos.cemea.org`, none of them sepiasearch.org
 *   itself, which hosts no videos of its own). `embedUrl` — an already-absolute embed address — is
 *   present on those federated hits and absent on a same-instance ("local") one, where `embedPath`
 *   joined onto `account.host` is the only way to reach it. `url` — the row's own **watch** page,
 *   `https://{host}/videos/watch/{uuid}` — rides both kinds of hit (measured 2026-08-06 on both
 *   servers), and that path answers `200` directly rather than redirecting to the `/w/{shortUUID}`
 *   spelling.
 * - `GET /api/v1/videos/{uuid|shortUUID}` answers the full video record; `streamingPlaylists` is an
 *   array of `{ type, playlistUrl, segmentsSha256Url, files: [...] }` — `type: 1` is HLS (PeerTube's
 *   `VideoStreamingPlaylistType.HLS`), and modern PeerTube serves exactly one entry, always HLS.
 *   `playlistUrl` is the `.m3u8` master the "integrated" mode feeds to Shaka. This request has to go
 *   to the result's own `instanceHost` — a search-index server such as sepiasearch.org answers its
 *   *own* `/api/v1/videos/{uuid}` with nothing for a video it does not host (measured: `total`/body
 *   absent entirely), because it never had the record, only the search index's copy of it.
 */

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hostOf(row: Readonly<Record<string, unknown>>, key: 'account' | 'channel'): string | null {
  const nested = row[key];
  const host = isRecord(nested) ? nested['host'] : null;
  return typeof host === 'string' && host.trim() !== '' ? host : null;
}

/** One search hit — enough to pick a match and, if picked, to embed or stream it. */
export interface PeerTubeSearchResult {
  readonly title: string;
  readonly durationSec: number;
  readonly uuid: string;
  /** Instance-relative — joined onto {@link instanceHost} when {@link embedUrl} is absent. */
  readonly embedPath: string;
  /** The result's own origin (`account.host`, falling back to `channel.host`) — not necessarily the
   *  instance that was searched; see the module doc. */
  readonly instanceHost: string;
  /** An already-absolute embed address, present on a federated hit; `null` on a same-instance one. */
  readonly embedUrl: string | null;
  /**
   * The row's own watch page (`url`), which every hit carries — federated or same-instance (measured
   * 2026-08-06: sepiasearch.org and framatube.org both answer it). `null` only when a payload omits
   * it, which `peerTubeWatchUrl` covers by building the address from the host and the uuid.
   */
  readonly watchUrl: string | null;
}

export function parsePeerTubeSearchResults(payload: unknown): readonly PeerTubeSearchResult[] {
  const data = isRecord(payload) ? payload['data'] : null;
  if (!Array.isArray(data)) return [];

  const out: PeerTubeSearchResult[] = [];
  for (const row of data) {
    if (!isRecord(row)) continue;
    const title = row['name'];
    const duration = row['duration'];
    const uuid = row['uuid'];
    const embedPath = row['embedPath'];
    const instanceHost = hostOf(row, 'account') ?? hostOf(row, 'channel');
    if (typeof title !== 'string' || title.trim() === '') continue;
    if (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) continue;
    if (typeof uuid !== 'string' || uuid.trim() === '') continue;
    if (typeof embedPath !== 'string' || embedPath.trim() === '') continue;
    if (instanceHost === null) continue;
    const embedUrlRaw = row['embedUrl'];
    const embedUrl = typeof embedUrlRaw === 'string' && embedUrlRaw.trim() !== '' ? embedUrlRaw : null;
    const watchUrlRaw = row['url'];
    const watchUrl = typeof watchUrlRaw === 'string' && watchUrlRaw.trim() !== '' ? watchUrlRaw : null;
    out.push({ title, durationSec: duration, uuid, embedPath, instanceHost, embedUrl, watchUrl });
  }
  return out;
}

/**
 * The reference match rule: the **exact** title, trimmed, and the length within 1 second —
 * PeerTube durations are whole seconds like Invidious's `lengthSeconds`, so the tolerance absorbs
 * rounding on either side rather than fuzzy-matching titles. The first result clearing both wins;
 * PeerTube's own relevance ranking decided the order, not this function.
 */
export function pickPeerTubeMatch(
  results: readonly PeerTubeSearchResult[],
  title: string,
  durationSec: number,
): PeerTubeSearchResult | null {
  const wanted = title.trim();
  if (wanted === '') return null;
  return (
    results.find(
      (result) => result.title.trim() === wanted && Math.abs(result.durationSec - durationSec) <= 1,
    ) ?? null
  );
}

function isHlsPlaylist(value: unknown): value is Readonly<Record<string, unknown>> {
  return isRecord(value) && value['type'] === 1;
}

/**
 * The HLS master playlist URL the "integrated" mode feeds to Shaka, or `null` when the video has
 * none (still transcoding, or an instance not serving HLS). `type: 1` is picked over "just the
 * first entry" defensively — modern PeerTube serves exactly one, always HLS, but a future instance
 * offering more than one playlist type should not have this pick the wrong one silently.
 */
export function peerTubeHlsPlaylistUrl(payload: unknown): string | null {
  if (!isRecord(payload)) return null;
  const playlists = payload['streamingPlaylists'];
  if (!Array.isArray(playlists)) return null;
  const first = playlists[0];
  const hls = playlists.find(isHlsPlaylist) ?? (isRecord(first) ? first : null);
  if (!isRecord(hls)) return null;
  const url = hls['playlistUrl'];
  return typeof url === 'string' && url.trim() !== '' ? url : null;
}
